import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { TrackLocations } from "@/hooks/use-segment";
import { useDeleteWorkflow, type PlainWorkflow } from "@/queries/automation-queries";
import { workflowName } from "@/utils/automations";
import { formatError } from "@/utils/errors";
import { useCallback, type FC } from "react";
import { useNavigate } from "react-router-dom";
import { WorkflowCard } from "@/components/automations/AutomationCard";
import { Banner } from "@/components/flexkit/Banner";

type DeleteWorkflowDialogProps = {
    workflow: PlainWorkflow;
    open: boolean;
    onClose: () => void;
    forceDelete?: boolean;
};

export const DeleteWorkflowDialog: FC<DeleteWorkflowDialogProps> = ({ workflow, open, onClose, forceDelete = false }) => {
    const { toast } = useToast();
    const navigate = useNavigate();
    const deleteWorkflow = useDeleteWorkflow();

    const handleDelete = useCallback(async () => {
        try {
            await deleteWorkflow.mutateAsync({ workflowId: workflow.id, force: forceDelete });
            toast({
                title: forceDelete ? "Automation force deleted" : "Automation deleted",
                description: forceDelete 
                    ? `${workflowName(workflow)} has been force deleted. Check your cloud account for any remaining resources.`
                    : `${workflowName(workflow)} has been deleted successfully.`,
            });
            onClose();
            navigate("/automations");
        } catch (error) {
            toast({
                title: "Failed to delete automation",
                description: formatError(error),
            });
        }
    }, [deleteWorkflow, workflow, toast, onClose, navigate, forceDelete]);

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent
                data-testid="delete-workflow-dialog"
                data-track-location={TrackLocations.DeleteWorkflowDialog}
            >
                <DialogHeader>
                    <DialogTitle>{forceDelete ? "Force Delete Workflow" : "Delete Workflow"}</DialogTitle>
                    <DialogDescription>
                        {forceDelete 
                            ? "Force deleting will immediately remove the workflow from the system without waiting for proper cleanup. This may leave cloud resources (VMs, storage, etc.) running in your cloud account."
                            : "Are you sure you want to delete this automation? This action cannot be undone."}
                    </DialogDescription>
                </DialogHeader>
                <DialogBody className="flex flex-col gap-3">
                    {forceDelete && (
                        <Banner
                            variant="warning"
                            text="Warning: Force delete bypasses the normal cleanup process and may leave cloud resources running that will continue to incur costs. Use this only if normal deletion is stuck."
                        />
                    )}
                    <WorkflowCard workflow={workflow} readonly />
                </DialogBody>
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" data-testid="delete-workflow-dialog-cancel">
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        variant="destructive"
                        onClick={handleDelete}
                        loading={deleteWorkflow.isPending}
                        data-testid="delete-workflow-dialog-confirm"
                        data-tracking-id={forceDelete ? "confirm-force-delete-workflow-delete-workflow-dialog" : "confirm-delete-workflow-delete-workflow-dialog"}
                    >
                        {forceDelete ? "Force Delete Workflow" : "Delete Workflow"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
